/**
 * Mock API layer for demo/testing when the backend is not available.
 * Activated automatically when API calls fail with network errors,
 * or when VITE_MOCK_API=true is set.
 *
 * Returns realistic document analysis and suggestion responses
 * based on actual document content sent by the widget.
 */

import type { ChatResponse, AnalyzeRequest, FollowUpRequest } from '@/types/chat'
import type { GenerateSuggestionsRequest, GenerateSuggestionsResponse } from '@/types/suggestion'
import type { LoginResponse } from '@/types/auth'
import { debugLog } from '@/debug'

// Track whether we're in mock mode
let _mockMode = import.meta.env.VITE_MOCK_API === 'true'

export function isMockMode(): boolean {
  return _mockMode
}

export function enableMockMode(): void {
  _mockMode = true
  debugLog.add('info', 'Mock API mode enabled (backend unavailable)')
}

function delay(ms = 800): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms + Math.random() * 400))
}

function uuid(): string {
  return crypto.randomUUID()
}

// ─── Auth ────────────────────────────────────────────────────────────

export async function mockLogin(email: string, _password: string): Promise<LoginResponse> {
  await delay(500)
  return {
    access_token: `mock-at-${Date.now()}`,
    refresh_token: `mock-rt-${Date.now()}`,
    token_type: 'Bearer',
    expires_in: 900,
    user: {
      id: uuid(),
      email,
      name: email.split('@')[0] || 'Demo',
      locale: 'es'
    }
  }
}

// ─── Chat ────────────────────────────────────────────────────────────

interface MockConversation {
  document: string
  messages: string[]
}
const conversations = new Map<string, MockConversation>()

function generateContractDraft(locale: 'en' | 'es'): string {
  if (locale === 'en') {
    return `Here is a draft lease agreement you can insert into your document:

**LEASE AGREEMENT**

In the city of __________, on the __ day of __________ 2026, the following parties appear:

**LANDLORD:** __________, identified with ID No. __________, domiciled at __________.

**TENANT:** __________, identified with ID No. __________, domiciled at __________.

The parties agree to enter into this Lease Agreement under the following terms:

**FIRST. PURPOSE:** The LANDLORD delivers to the TENANT, under a lease agreement, the property located at __________, consisting of __________.

**SECOND. RENT:** The monthly rent shall be $__________, payable within the first five (5) days of each month via __________.

**THIRD. TERM:** This agreement shall be valid for __________ months, starting from __________.

**FOURTH. SECURITY DEPOSIT:** The TENANT delivers the sum of $__________ as a security deposit, which shall be returned at the end of the lease, less any deductions for damages.

**FIFTH. OBLIGATIONS OF THE TENANT:**
1. Pay rent on time.
2. Maintain the property in good condition.
3. Not sublease without prior written consent from the LANDLORD.
4. Allow inspections with prior notice.

**SIXTH. OBLIGATIONS OF THE LANDLORD:**
1. Deliver the property in habitable condition.
2. Perform structural repairs.
3. Respect the tenant's peaceful enjoyment.

**SEVENTH. TERMINATION:** Either party may terminate this agreement with __________ days' written notice.

**EIGHTH. JURISDICTION:** For any dispute, the parties submit to the jurisdiction of __________.

Signed in agreement,

LANDLORD: __________
TENANT: __________

---

*Use the "Insert into document" button to add this to your Word document. You can then customize the blank fields.*`
  }

  return `Aquí tienes un borrador de contrato de arrendamiento que puedes insertar en tu documento:

**CONTRATO DE ARRENDAMIENTO**

En la ciudad de __________, a los __ días del mes de __________ de 2026, comparecen:

**ARRENDADOR:** __________, identificado con cédula de ciudadanía No. __________, domiciliado en __________.

**ARRENDATARIO:** __________, identificado con cédula de ciudadanía No. __________, domiciliado en __________.

Las partes acuerdan celebrar el presente contrato de arrendamiento, que se regirá por las siguientes cláusulas:

**PRIMERA. OBJETO:** El ARRENDADOR entrega al ARRENDATARIO a título de arrendamiento el inmueble ubicado en __________, el cual consta de __________.

**SEGUNDA. CANON:** El canon mensual de arrendamiento es de $__________ M/CTE, pagadero dentro de los primeros cinco (5) días de cada mes, mediante __________.

**TERCERA. TÉRMINO:** El plazo del presente contrato es de __________ meses, contados a partir del __________.

**CUARTA. DEPÓSITO:** El ARRENDATARIO entrega la suma de $__________ como depósito de garantía, el cual será devuelto al finalizar el contrato, descontando los daños que se presenten.

**QUINTA. OBLIGACIONES DEL ARRENDATARIO:**
1. Pagar el canon de arrendamiento oportunamente.
2. Mantener el inmueble en buen estado.
3. No subarrendar sin autorización escrita del ARRENDADOR.
4. Permitir inspecciones previo aviso.

**SEXTA. OBLIGACIONES DEL ARRENDADOR:**
1. Entregar el inmueble en condiciones habitables.
2. Realizar reparaciones estructurales.
3. Respetar el uso pacífico del inmueble.

**SÉPTIMA. TERMINACIÓN:** Cualquiera de las partes podrá dar por terminado el contrato con __________ días de anticipación mediante aviso escrito.

**OCTAVA. JURISDICCIÓN:** Para cualquier controversia, las partes se someten a la jurisdicción de __________.

Se firma en señal de conformidad,

ARRENDADOR: __________
ARRENDATARIO: __________

---

*Usa el botón "Insertar en documento" para agregar este contenido a tu documento de Word. Luego puedes personalizar los campos en blanco.*`
}

function isSummaryRequest(msg: string): boolean {
  return /resum|summar|sinopsis|síntesis|resúm|de qué|de que|qué es|que es|what is|what'?s this|about what|qué tipo|que tipo|what type|what kind|descri/i.test(msg)
}

function isQuestionAboutDocument(msg: string): boolean {
  return /\?|de qué|de que|qué es|que es|what is|cuál|cual|cómo|como|cuánto|cuanto|quién|quien|dónde|donde|cuándo|cuando|explain|explica|analiz|analy/i.test(msg)
}

function isCreationRequest(msg: string): boolean {
  // Don't match creation keywords when the user is asking for a summary/analysis
  if (isSummaryRequest(msg)) return false
  return /contrato|arrendamiento|crear|crea|genera|redact|escrib|plantilla|draft|create|write|generate|template|lease|contract|document from scratch/i.test(msg)
}

function isUpdateRequest(msg: string): boolean {
  return /actualiz|actualic|cambi\w|reemplaz|sustitu|modific|pon\s|coloca|establec|llena|complet|rellen|fill|update|change|set\s|put\s|replace/i.test(msg)
}

function isAddRequest(msg: string): boolean {
  return /agrega|añad|incluye|inserta|pon\s+una|add\s|include|insert|append/i.test(msg)
}

function isGreeting(msg: string): boolean {
  const trimmed = msg.trim().replace(/[.!?¡¿,]+$/g, '').trim().toLowerCase()
  // Short casual messages or common greetings
  if (trimmed.length <= 15 && /^(hi|hey|hello|hola|buenos?\s*días?|buenas?\s*tardes?|buenas?\s*noches?|saludos|qué tal|que tal|sup|yo|ey)$/i.test(trimmed)) return true
  // Slightly longer greetings
  return /^(hi|hey|hello|hola|buenos?\s*días?|buenas?|saludos|qué tal|que tal)\b/i.test(trimmed) && trimmed.split(/\s+/).length <= 4
}

function greetWithDocumentContext(content: string, locale: 'en' | 'es'): string {
  const type = detectDocumentType(content, locale)
  if (locale === 'en') {
    return `Hello! I see you have a **${type}** document open. How can I help you?\n\nFor example, you can ask me to:\n- **Summarize** the document\n- **Analyze risks** or key clauses\n- **Suggest edits** to improve it\n- **Answer questions** about its content`
  }
  return `¡Hola! Veo que tienes un documento de tipo **${type}** abierto. ¿En qué puedo ayudarte?\n\nPor ejemplo, puedes pedirme:\n- **Resumir** el documento\n- **Analizar riesgos** o cláusulas clave\n- **Sugerir ediciones** para mejorarlo\n- **Responder preguntas** sobre su contenido`
}

/**
 * Generate a content-aware summary of a document based on its text.
 */
function summarizeDocument(content: string, locale: 'en' | 'es'): string {
  const wordCount = content.split(/\s+/).length
  const lineCount = content.split('\n').filter(l => l.trim()).length
  const truncated = content.length > 300 ? content.slice(0, 300) + '...' : content

  // Detect document type by keywords
  const type = detectDocumentType(content, locale)

  if (locale === 'en') {
    return `**Document Summary**\n\nThis appears to be a **${type}** document with approximately ${wordCount} words organized in ${lineCount} sections.\n\n**Content overview:**\n> ${truncated}\n\nWould you like me to analyze any specific aspect of this document, or generate edit suggestions?`
  }
  return `**Resumen del Documento**\n\nEste parece ser un documento de tipo **${type}** con aproximadamente ${wordCount} palabras organizadas en ${lineCount} secciones.\n\n**Vista general del contenido:**\n> ${truncated}\n\n¿Te gustaría que analice algún aspecto específico de este documento, o que genere sugerencias de edición?`
}

/**
 * Describe what a document is about based on keyword detection.
 */
function describeDocument(content: string, locale: 'en' | 'es'): string {
  const type = detectDocumentType(content, locale)
  const wordCount = content.split(/\s+/).length
  const truncated = content.length > 200 ? content.slice(0, 200) + '...' : content

  if (locale === 'en') {
    return `This is a **${type}** document with ${wordCount} words.\n\n**Preview:**\n> ${truncated}\n\nI can help you analyze it, suggest improvements, or make specific changes. What would you like to do?`
  }
  return `Este es un documento de tipo **${type}** con ${wordCount} palabras.\n\n**Vista previa:**\n> ${truncated}\n\nPuedo ayudarte a analizarlo, sugerir mejoras o hacer cambios específicos. ¿Qué te gustaría hacer?`
}

/**
 * Detect the type/category of a document from its content.
 */
function detectDocumentType(content: string, locale: 'en' | 'es'): string {
  const lower = content.toLowerCase()
  // Check specific document types before generic ones (e.g., "laboral" before "contrato")
  if (/laboral|trabajo|empleador|empleado|salario|labor|employment|employer|employee|salary|wages/i.test(lower))
    return locale === 'en' ? 'employment/labor document' : 'documento laboral'
  if (/arrendamiento|lease|arrendador|arrendatario|canon|rent/i.test(lower))
    return locale === 'en' ? 'lease agreement' : 'contrato de arrendamiento'
  if (/contrato|contract|agreement|acuerdo|cláusula|clause/i.test(lower))
    return locale === 'en' ? 'contract' : 'contrato'
  if (/factura|invoice|total|subtotal|iva|tax/i.test(lower))
    return locale === 'en' ? 'invoice' : 'factura'
  if (/acta|minutes|reunión|meeting|asistentes|attendees/i.test(lower))
    return locale === 'en' ? 'meeting minutes' : 'acta de reunión'
  if (/carta|letter|estimado|dear|atentamente|sincerely/i.test(lower))
    return locale === 'en' ? 'letter' : 'carta'
  if (/informe|report|análisis|analysis|resultados|results|conclusiones|conclusions/i.test(lower))
    return locale === 'en' ? 'report' : 'informe'
  if (/tarea|task|pendiente|pending|completad|to.?do/i.test(lower))
    return locale === 'en' ? 'task list' : 'lista de tareas'
  if (/poder|notari|escritura|power of attorney|notary/i.test(lower))
    return locale === 'en' ? 'notarial document' : 'documento notarial'
  return locale === 'en' ? 'general' : 'general'
}

/**
 * Generate a response that acknowledges the user's message and analyzes the document accordingly.
 * This is the catch-all for any message + document combination that didn't match a specific handler.
 */
function generateMessageAwareAnalysis(content: string, message: string, locale: 'en' | 'es'): string {
  const type = detectDocumentType(content, locale)
  const wordCount = content.split(/\s+/).length
  const lineCount = content.split('\n').filter(l => l.trim()).length
  const truncated = content.length > 300 ? content.slice(0, 300) + '...' : content

  if (locale === 'en') {
    return `I've analyzed your **${type}** document (${wordCount} words, ${lineCount} sections) based on your request: "${message}"

**Content overview:**
> ${truncated}

**Analysis:**
- The document is structured in ${lineCount} sections with ${wordCount} words total.
- Based on your request, I can help you review, modify, or improve specific aspects of this document.

You can:
- Ask me specific questions about the content
- Request a summary or risk analysis
- Click **"Generate edit changes"** for detailed suggestions

What would you like me to focus on?`
  }

  return `He analizado tu documento de tipo **${type}** (${wordCount} palabras, ${lineCount} secciones) en base a tu solicitud: "${message}"

**Vista general del contenido:**
> ${truncated}

**Análisis:**
- El documento está estructurado en ${lineCount} secciones con ${wordCount} palabras en total.
- Basándome en tu solicitud, puedo ayudarte a revisar, modificar o mejorar aspectos específicos de este documento.

Puedes:
- Hacerme preguntas específicas sobre el contenido
- Solicitar un resumen o análisis de riesgos
- Hacer clic en **"Generar cambios de edición"** para sugerencias detalladas

¿En qué te gustaría que me enfoque?`
}

interface TextReplacement {
  original: string
  replacement: string
}

/**
 * Extract generic text replacement requests from a message.
 * Handles patterns like "cambia X por Y", "reemplaza X con Y", "change X to Y".
 */
function extractReplacements(msg: string): TextReplacement[] {
  const replacements: TextReplacement[] = []

  const patterns = [
    // Spanish: cambia/reemplaza/sustituye X por/con Y
    /(?:cambi\w*|reemplaz\w*|sustitu\w*)\s+(?:(?:la |el )?(?:palabra |texto ))?["«\u201C]?(.+?)["»\u201D]?\s+(?:por|con)\s+["«\u201C]?(.+?)["»\u201D]?\s*$/gim,
    // English: change/replace/swap X to/with Y
    /(?:change|replace|swap)\s+(?:(?:the )?(?:word |text ))?["\u201C]?(.+?)["\u201D]?\s+(?:to|with|for|by)\s+["\u201C]?(.+?)["\u201D]?\s*$/gim,
  ]

  for (const pattern of patterns) {
    for (const match of msg.matchAll(pattern)) {
      const orig = match[1]?.trim()
      const repl = match[2]?.trim()
      if (orig && repl) {
        replacements.push({ original: orig, replacement: repl })
      }
    }
  }

  return replacements
}

function respondToMessageWithDocument(_content: string, message: string, locale: 'en' | 'es'): string | null {
  const fields = extractFieldData(message)
  const hasFields = Object.keys(fields).length > 0
  const lower = message.toLowerCase()

  // User wants to fill in fields (e.g., "pon la fecha de hoy y el arrendador es ...")
  if (hasFields) {
    const changes: string[] = []
    if (fields.date) changes.push(locale === 'en' ? `**Date:** ${fields.date}` : `**Fecha:** ${fields.date}`)
    if (fields.city) changes.push(locale === 'en' ? `**City:** ${fields.city}` : `**Ciudad:** ${fields.city}`)
    if (fields.landlordName) changes.push(locale === 'en' ? `**Landlord:** ${fields.landlordName}` : `**Arrendador:** ${fields.landlordName}`)
    if (fields.landlordId) changes.push(locale === 'en' ? `**Landlord ID:** ${fields.landlordId}` : `**Cédula arrendador:** ${fields.landlordId}`)
    if (fields.tenantName) changes.push(locale === 'en' ? `**Tenant:** ${fields.tenantName}` : `**Arrendatario:** ${fields.tenantName}`)
    if (fields.tenantId) changes.push(locale === 'en' ? `**Tenant ID:** ${fields.tenantId}` : `**Cédula arrendatario:** ${fields.tenantId}`)
    if (fields.amount) changes.push(locale === 'en' ? `**Amount:** $${fields.amount}` : `**Canon/Monto:** $${fields.amount}`)
    if (fields.address) changes.push(locale === 'en' ? `**Address:** ${fields.address}` : `**Dirección:** ${fields.address}`)

    if (locale === 'en') {
      return `I've identified the following data to update in your document:\n\n${changes.join('\n')}\n\nClick **"Generate edit changes"** to apply these changes to the document automatically.`
    }
    return `He identificado los siguientes datos para actualizar en tu documento:\n\n${changes.join('\n')}\n\nHaz clic en **"Generar cambios de edición"** para aplicar estos cambios automáticamente en el documento.`
  }

  // User wants to replace specific text (e.g., "cambia nombre por Angélica")
  const replacements = extractReplacements(message)
  if (replacements.length > 0) {
    const changes = replacements.map(r => `- **"${r.original}"** → **"${r.replacement}"**`)
    if (locale === 'en') {
      return `I've identified the following text replacements:\n\n${changes.join('\n')}\n\nClick **"Generate edit changes"** to apply these changes to the document automatically.`
    }
    return `He identificado los siguientes reemplazos de texto:\n\n${changes.join('\n')}\n\nHaz clic en **"Generar cambios de edición"** para aplicar estos cambios automáticamente en el documento.`
  }

  // User wants to update/modify something specific
  if (isUpdateRequest(lower)) {
    if (locale === 'en') {
      return `I understand you want to make changes to the document. Click **"Generate edit changes"** and I'll create specific edits based on your request.\n\nIf you need to fill in specific fields, you can tell me something like:\n- "Set the date to today and the landlord is John Smith ID 12345"\n- "Change the term to 12 months"`
    }
    return `Entiendo que quieres hacer cambios en el documento. Haz clic en **"Generar cambios de edición"** y crearé ediciones específicas basadas en tu solicitud.\n\nSi necesitas llenar campos específicos, puedes decirme algo como:\n- "Pon la fecha de hoy y el arrendador es Juan Pérez cc 123456"\n- "Cambia el plazo a 12 meses"`
  }

  // User wants to add content
  if (isAddRequest(lower)) {
    if (locale === 'en') {
      return `I can add that content to your document. Click **"Generate edit changes"** and I'll prepare the addition as an edit suggestion you can review and apply.`
    }
    return `Puedo agregar ese contenido a tu documento. Haz clic en **"Generar cambios de edición"** y prepararé la adición como una sugerencia de edición que puedes revisar y aplicar.`
  }

  // Generic question about the document — give contextual analysis
  // No specific intent detected — caller should use generic analysis
  return null
}

function analyzeContent(content: string, message: string | undefined, locale: 'en' | 'es'): string {
  // Handle empty document — the user wants to create content
  if (!content.trim()) {
    const userMsg = message || ''

    // If the user asks to create something specific, generate it directly
    if (userMsg && isCreationRequest(userMsg)) {
      return generateContractDraft(locale)
    }

    if (locale === 'en') {
      return `The document is empty. I can help you create content.\n\nTell me what kind of document you'd like to create — for example: a contract, letter, report, or any other document — and I'll generate a draft for you.`
    }
    return `El documento está vacío. Puedo ayudarte a crear contenido.\n\nDime qué tipo de documento te gustaría crear — por ejemplo: un contrato, carta, informe, o cualquier otro documento — y te generaré un borrador.`
  }

  // When user provides a message with document content, ALWAYS respond to the message
  // Never fall through to generic "Análisis del Documento" when the user asked something specific
  if (message?.trim()) {
    // Try specific intent handlers first (field-filling, text replacement, update, add)
    const contextualResponse = respondToMessageWithDocument(content, message, locale)
    if (contextualResponse) return contextualResponse

    // Summary requests
    if (isSummaryRequest(message)) {
      return summarizeDocument(content, locale)
    }

    // Greetings / short casual messages — respond conversationally
    if (isGreeting(message)) {
      return greetWithDocumentContext(content, locale)
    }

    // Any other message with a document — generate a message-aware analysis
    // that acknowledges what the user asked and responds about the document
    return generateMessageAwareAnalysis(content, message, locale)
  }

  // Generic "Document Analysis" ONLY when NO message is provided (pure document submission)
  const wordCount = content.split(/\s+/).length
  const lineCount = content.split('\n').filter(l => l.trim()).length
  const truncated = content.length > 200 ? content.slice(0, 200) + '...' : content

  if (locale === 'en') {
    return `**Document Analysis**

I've reviewed your document (${wordCount} words, ${lineCount} sections). Here are my observations:

**Structure:** The document contains ${lineCount} distinct sections. ${lineCount > 3 ? 'The organization is generally clear, though some sections could benefit from more descriptive headers.' : 'Consider expanding with additional sections for completeness.'}

**Content Preview:**
> ${truncated}

**Recommendations:**
1. Review the overall structure for logical flow between sections.
2. Consider adding more detail to placeholder items (marked with "...").
3. Ensure all tasks are clearly described with responsible parties and deadlines.

I can provide more specific suggestions if you'd like. You can also generate edit changes using the "Generate edit changes" button.`
  }

  return `**Análisis del Documento**

He revisado tu documento (${wordCount} palabras, ${lineCount} secciones). Aquí están mis observaciones:

**Estructura:** El documento contiene ${lineCount} secciones distintas. ${lineCount > 3 ? 'La organización es generalmente clara, aunque algunas secciones podrían beneficiarse de encabezados más descriptivos.' : 'Considera expandir con secciones adicionales para mayor completitud.'}

**Vista previa del contenido:**
> ${truncated}

**Recomendaciones:**
1. Revisa la estructura general para un flujo lógico entre secciones.
2. Considera agregar más detalle a los elementos con marcadores de posición (marcados con "...").
3. Asegúrate de que todas las tareas estén claramente descritas con responsables y plazos.

Puedo proporcionar cambios más específicos si lo deseas. También puedes generar cambios de edición usando el botón "Generar cambios de edición".`
}

/**
 * Extract specific data the user wants to fill in from their message.
 * Returns an object with field names and extracted values.
 */
function extractFieldData(msg: string): Record<string, string> {
  const fields: Record<string, string> = {}

  // Extract date: "fecha de hoy", "hoy", "today", or specific dates
  if (/fecha\s*de\s*hoy|hoy|today/i.test(msg)) {
    const now = new Date()
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
    fields.date = `${now.getDate()} de ${months[now.getMonth()]} de ${now.getFullYear()}`
  }

  // Extract names: "arrendador es X" or "arrendatario es X"
  const arrendadorMatch = msg.match(/arrendador\s+(?:es|será|:)\s+([^,\n]+?)(?:\s+(?:cc|cédula|c\.c\.|identificad|con)\s|$)/i)
  if (arrendadorMatch) fields.landlordName = arrendadorMatch[1]!.trim()

  const arrendatarioMatch = msg.match(/arrendatario\s+(?:es|será|:)\s+([^,\n]+?)(?:\s+(?:cc|cédula|c\.c\.|identificad|con)\s|$)/i)
  if (arrendatarioMatch) fields.tenantName = arrendatarioMatch[1]!.trim()

  // Extract ID numbers: "cc 123456", "cédula 123456"
  const idMatches = [...msg.matchAll(/(?:cc|cédula|c\.c\.?)\s*:?\s*(\d[\d.]+)/gi)]
  if (idMatches.length >= 1) fields.landlordId = idMatches[0]![1]!
  if (idMatches.length >= 2) fields.tenantId = idMatches[1]![1]!

  // If name is followed directly by "cc", capture the name before it
  if (!fields.landlordName) {
    const nameBeforeCC = msg.match(/arrendador\s+(?:es|será|:)\s+(.+?)\s+cc\s/i)
    if (nameBeforeCC) fields.landlordName = nameBeforeCC[1]!.trim()
  }
  if (!fields.tenantName) {
    const nameBeforeCC = msg.match(/arrendatario\s+(?:es|será|:)\s+(.+?)\s+cc\s/i)
    if (nameBeforeCC) fields.tenantName = nameBeforeCC[1]!.trim()
  }

  // Extract amounts: "canon de $X" or "valor $X" or just "$X"
  const amountMatch = msg.match(/\$\s*([\d.,]+)/i)
  if (amountMatch) fields.amount = amountMatch[1]!

  // Extract address
  const addressMatch = msg.match(/(?:dirección|ubicad[oa]|inmueble)\s+(?:es|en|:)\s+(.+?)(?:\.|$)/i)
  if (addressMatch) fields.address = addressMatch[1]!.trim()

  // Extract city
  const cityMatch = msg.match(/ciudad\s+(?:de|es|:)\s+(\w+)/i)
  if (cityMatch) fields.city = cityMatch[1]!

  return fields
}

function respondToFollowUp(message: string, locale: 'en' | 'es', document?: string): string {
  const lowerMsg = message.toLowerCase()

  // Greetings — respond conversationally
  if (isGreeting(message)) {
    if (document?.trim()) return greetWithDocumentContext(document, locale)
    if (locale === 'en') return 'Hello! Send me a document and I can help you analyze, summarize, or improve it.'
    return '¡Hola! Envíame un documento y puedo ayudarte a analizarlo, resumirlo o mejorarlo.'
  }

  // Fix 913: Check summary/question requests BEFORE creation requests
  if (isSummaryRequest(lowerMsg)) {
    if (document?.trim()) return summarizeDocument(document, locale)
    if (locale === 'en') return 'Could you send the document first so I can generate a summary? Use the "Full Document" option to share it with me.'
    return 'Podrías enviar el documento primero para que pueda generar un resumen? Usa la opción "Documento completo" para compartirlo conmigo.'
  }

  // Fix 914: Handle general questions about the document
  if (isQuestionAboutDocument(lowerMsg) && document?.trim()) {
    return describeDocument(document, locale)
  }

  // Handle creation requests in any language
  if (isCreationRequest(lowerMsg)) {
    return generateContractDraft(locale)
  }

  // Detect field-filling requests (user wants to update specific data)
  const fields = extractFieldData(message)
  if (Object.keys(fields).length > 0) {
    const changes: string[] = []
    if (fields.date) changes.push(`**Fecha:** ${fields.date}`)
    if (fields.city) changes.push(`**Ciudad:** ${fields.city}`)
    if (fields.landlordName) changes.push(`**Arrendador:** ${fields.landlordName}`)
    if (fields.landlordId) changes.push(`**Cédula arrendador:** ${fields.landlordId}`)
    if (fields.tenantName) changes.push(`**Arrendatario:** ${fields.tenantName}`)
    if (fields.tenantId) changes.push(`**Cédula arrendatario:** ${fields.tenantId}`)
    if (fields.amount) changes.push(`**Canon/Monto:** $${fields.amount}`)
    if (fields.address) changes.push(`**Dirección:** ${fields.address}`)

    if (locale === 'en') {
      return `I've identified the following data to update in the document:\n\n${changes.join('\n')}\n\n*In production, Korus would apply these changes directly to the blank fields in your document. For now, you can manually update the fields using the information above.*`
    }

    return `He identificado los siguientes datos para actualizar en el documento:\n\n${changes.join('\n')}\n\n*En producción, Korus aplicaría estos cambios directamente en los campos en blanco de tu documento. Por ahora, puedes actualizar manualmente los campos con la información anterior.*`
  }

  if (locale === 'en') {
    if (lowerMsg.includes('grammar') || lowerMsg.includes('error'))
      return 'Based on my review, the document has a clean grammatical structure. The main area for improvement is completing the placeholder items marked with "..." — once filled in, I can provide more targeted grammar suggestions. Would you like me to generate detailed edit suggestions?'
    if (lowerMsg.includes('improve') || lowerMsg.includes('better'))
      return 'To improve this document, I recommend:\n\n1. **Replace placeholders** — All items marked "..." should be filled with specific, actionable content.\n2. **Add deadlines** — Each task should have a clear due date.\n3. **Include priorities** — Consider adding priority levels (high/medium/low) to help with task management.\n4. **Add context** — Brief descriptions for each task will improve clarity.'
    // Fallback for English — offer contextual help, not just "give me more details"
    if (document?.trim()) return describeDocument(document, 'en')
    return `I understand your request. Could you provide more details about what specific changes you'd like? For example:\n\n- **Fill in fields:** "Set the date to today and the landlord is John Smith ID 12345"\n- **Add content:** "Add a clause about pets"\n- **Modify text:** "Change the term to 12 months"\n\nI'll do my best to help.`
  }

  if (lowerMsg.includes('gramática') || lowerMsg.includes('error'))
    return 'Según mi revisión, el documento tiene una estructura gramatical limpia. El área principal de mejora es completar los elementos marcados con "..." — una vez completados, puedo proporcionar cambios más específicos. ¿Te gustaría que genere cambios de edición detallados?'
  if (lowerMsg.includes('mejorar') || lowerMsg.includes('mejor'))
    return 'Para mejorar este documento, recomiendo:\n\n1. **Reemplazar marcadores** — Todos los elementos con "..." deben llenarse con contenido específico y accionable.\n2. **Agregar plazos** — Cada tarea debería tener una fecha límite clara.\n3. **Incluir prioridades** — Considera agregar niveles de prioridad (alta/media/baja).\n4. **Agregar contexto** — Descripciones breves para cada tarea mejorarán la claridad.'
  // Fix 914: Fallback for Spanish — describe the document instead of asking for edit details
  if (document?.trim()) return describeDocument(document, 'es')
  return `Entiendo tu solicitud. ¿Podrías darme más detalles sobre lo que necesitas? Por ejemplo:\n\n- **Resumen:** "Hazme un resumen del documento"\n- **Análisis:** "Analiza los riesgos del documento"\n- **Cambios:** "Cambia el plazo a 12 meses"\n\nHaré mi mejor esfuerzo para ayudarte.`
}

export async function mockAnalyzeDocument(request: AnalyzeRequest): Promise<ChatResponse> {
  await delay()
  const convId = request.conversation_id || uuid()
  const existing = conversations.get(convId)
  const messages = existing?.messages || []
  if (request.message) messages.push(request.message)
  conversations.set(convId, { document: request.document_content, messages })

  const hasContent = !!request.document_content.trim()
  return {
    conversation_id: convId,
    response: {
      message: analyzeContent(request.document_content, request.message, request.locale),
      suggestions_available: hasContent
    }
  }
}

export async function mockSendFollowUp(request: FollowUpRequest): Promise<ChatResponse> {
  await delay()
  const conv = conversations.get(request.conversation_id)
  if (conv) conv.messages.push(request.message)

  // If conversation has document content, respond contextually to the document
  if (conv?.document.trim()) {
    const contextualResponse = respondToMessageWithDocument(conv.document, request.message, request.locale)
    if (contextualResponse) {
      return {
        conversation_id: request.conversation_id,
        response: { message: contextualResponse, suggestions_available: true }
      }
    }
  }

  return {
    conversation_id: request.conversation_id,
    response: {
      message: respondToFollowUp(request.message, request.locale, conv?.document),
      suggestions_available: true
    }
  }
}

// ─── Suggestions ─────────────────────────────────────────────────────

/**
 * Extract sentences from a document. Splits on common sentence-ending
 * punctuation and filters out very short fragments.
 */
function extractSentences(text: string): string[] {
  return text
    .split(/(?<=[.!?;])\s+/)
    .map(s => s.trim())
    .filter(s => s.length >= 20 && s.length <= 200)
}

/**
 * Generic suggestion generators that work with any document content.
 * Each function scans the text for a specific pattern and returns
 * a suggestion (or null) using real text from the document.
 */
type SuggestionGen = (content: string, locale: 'en' | 'es') => GenerateSuggestionsResponse['suggestions'][number] | null

const genericGenerators: SuggestionGen[] = [
  // 1. Find very long sentences (>150 chars) and suggest splitting
  (content, locale) => {
    const sentences = extractSentences(content)
    const long = sentences.find(s => s.length > 150)
    if (!long) return null
    // Suggest trimming to first clause
    const cutPoint = long.indexOf(',', 60)
    if (cutPoint === -1) return null
    const shortened = long.slice(0, cutPoint) + '.'
    return {
      id: uuid(),
      original_text: long,
      suggested_text: shortened,
      reason: locale === 'en'
        ? 'Long sentences reduce readability. Consider breaking this into shorter sentences.'
        : 'Las oraciones largas reducen la legibilidad. Considera dividirla en oraciones más cortas.',
      category: 'clarity',
      severity: 'medium'
    }
  },

  // 2. Find double spaces
  (content, locale) => {
    const match = content.match(/.{10,30}  .{10,30}/)
    if (!match) return null
    const original = match[0]
    const fixed = original.replace(/  +/g, ' ')
    return {
      id: uuid(),
      original_text: original,
      suggested_text: fixed,
      reason: locale === 'en'
        ? 'Remove extra spaces for consistent formatting.'
        : 'Eliminar espacios dobles para formato consistente.',
      category: 'grammar',
      severity: 'low'
    }
  },

  // 3. Find ALL-CAPS text and suggest title case
  (content, locale) => {
    const match = content.match(/\b([A-ZÁÉÍÓÚÑÜ]{4,}(?:\s+[A-ZÁÉÍÓÚÑÜ]{3,}){1,6})\b/)
    if (!match) return null
    const original = match[1]!
    const titleCase = original
      .split(/\s+/)
      .map(w => w.charAt(0) + w.slice(1).toLowerCase())
      .join(' ')
    if (original === titleCase) return null
    return {
      id: uuid(),
      original_text: original,
      suggested_text: titleCase,
      reason: locale === 'en'
        ? 'Use title case instead of all caps for better readability.'
        : 'Usar capitalización de título en lugar de mayúsculas para mejor legibilidad.',
      category: 'style',
      severity: 'low'
    }
  },

  // 4. Find "etc." or "etc" and suggest being more specific
  (content, locale) => {
    const match = content.match(/.{15,40}(?:etc\.?|etcétera)/)
    if (!match) return null
    const original = match[0]
    const fixed = original.replace(/,?\s*(?:etc\.?|etcétera)\s*/g, '. ')
    return {
      id: uuid(),
      original_text: original,
      suggested_text: fixed.trim(),
      reason: locale === 'en'
        ? '"etc." is vague — list the remaining items explicitly or remove.'
        : '"etc." es vago — enumera los elementos restantes o elimínalo.',
      category: 'clarity',
      severity: 'medium'
    }
  },

  // 5. Find parenthetical text and suggest rewriting
  (content, locale) => {
    const match = content.match(/[^(]{10,30}\([^)]{10,60}\)[^)]{0,20}/)
    if (!match) return null
    const original = match[0]
    const withoutParens = original.replace(/\s*\([^)]+\)\s*/g, ' ').replace(/\s+/g, ' ')
    if (withoutParens.trim() === original.trim()) return null
    return {
      id: uuid(),
      original_text: original,
      suggested_text: withoutParens.trim(),
      reason: locale === 'en'
        ? 'Consider integrating parenthetical content into the main sentence for smoother reading.'
        : 'Considera integrar el contenido entre paréntesis en la oración principal para una lectura más fluida.',
      category: 'style',
      severity: 'low'
    }
  },

  // 6. Find a sentence and suggest a stylistic rewrite (generic demo)
  (content, locale) => {
    const sentences = extractSentences(content)
    // Pick a sentence from the middle of the document
    if (sentences.length < 3) return null
    const idx = Math.floor(sentences.length / 2)
    const original = sentences[idx]!
    // Simple rewrite: add a leading connector
    const rewrite = locale === 'en'
      ? `Furthermore, ${original.charAt(0).toLowerCase()}${original.slice(1)}`
      : `Además, ${original.charAt(0).toLowerCase()}${original.slice(1)}`
    return {
      id: uuid(),
      original_text: original,
      suggested_text: rewrite,
      reason: locale === 'en'
        ? 'Adding a transition word improves flow between ideas.'
        : 'Agregar una palabra de transición mejora el flujo entre ideas.',
      category: 'style',
      severity: 'low'
    }
  }
]

/**
 * Generate context-aware suggestions based on conversation history.
 * When the user asked to update fields (date, names, IDs), generate
 * suggestions that replace placeholder text in the document.
 */
function generateContextualSuggestions(
  content: string,
  conversationMessages: string[],
  locale: 'en' | 'es'
): GenerateSuggestionsResponse['suggestions'] {
  const suggestions: GenerateSuggestionsResponse['suggestions'] = []
  // Combine all user messages to extract field data and replacements
  const allMessages = conversationMessages.join(' ')
  const fields = extractFieldData(allMessages)
  const replacements = extractReplacements(allMessages)

  if (!Object.keys(fields).length && replacements.length === 0) return suggestions

  // Date: replace the main header date placeholder only
  if (fields.date) {
    const dateMatch = content.match(/a los __ días del mes de __________ de \d{4}/)
    if (dateMatch) {
      suggestions.push({
        id: uuid(),
        original_text: dateMatch[0],
        suggested_text: `a los ${fields.date.replace(/ de \d{4}$/, '')} de ${new Date().getFullYear()}`,
        reason: locale === 'en'
          ? 'Fill in the date as requested by the user.'
          : 'Completar la fecha según lo solicitado por el usuario.',
        category: 'clarity',
        severity: 'high'
      })
    }
  }

  // City
  if (fields.city) {
    const cityMatch = content.match(/En la ciudad de __________/)
    if (cityMatch) {
      suggestions.push({
        id: uuid(),
        original_text: cityMatch[0],
        suggested_text: `En la ciudad de ${fields.city}`,
        reason: locale === 'en'
          ? 'Fill in city name as requested.'
          : 'Completar el nombre de la ciudad según lo solicitado.',
        category: 'clarity',
        severity: 'high'
      })
    }
  }

  // Landlord name and ID
  if (fields.landlordName) {
    // Find the ARRENDADOR line with placeholder
    const landlordLine = content.match(/ARRENDADOR:\s*__________,\s*identificado con cédula de ciudadanía No\.\s*__________/)
    if (landlordLine) {
      let replacement = `ARRENDADOR: ${fields.landlordName}, identificado con cédula de ciudadanía No. ${fields.landlordId || '__________'}`
      suggestions.push({
        id: uuid(),
        original_text: landlordLine[0],
        suggested_text: replacement,
        reason: locale === 'en'
          ? 'Fill in landlord information as requested.'
          : 'Completar datos del arrendador según lo solicitado.',
        category: 'clarity',
        severity: 'high'
      })
    }
    // Also replace signature line
    const sigMatch = content.match(/ARRENDADOR: __________\s*$/)
    if (sigMatch) {
      suggestions.push({
        id: uuid(),
        original_text: 'ARRENDADOR: __________',
        suggested_text: `ARRENDADOR: ${fields.landlordName}`,
        reason: locale === 'en'
          ? 'Fill in landlord signature as requested.'
          : 'Completar firma del arrendador.',
        category: 'clarity',
        severity: 'medium'
      })
    }
  }

  // Tenant name and ID
  if (fields.tenantName) {
    const tenantLine = content.match(/ARRENDATARIO:\s*__________,\s*identificado con cédula de ciudadanía No\.\s*__________/)
    if (tenantLine) {
      let replacement = `ARRENDATARIO: ${fields.tenantName}, identificado con cédula de ciudadanía No. ${fields.tenantId || '__________'}`
      suggestions.push({
        id: uuid(),
        original_text: tenantLine[0],
        suggested_text: replacement,
        reason: locale === 'en'
          ? 'Fill in tenant information as requested.'
          : 'Completar datos del arrendatario según lo solicitado.',
        category: 'clarity',
        severity: 'high'
      })
    }
    const sigMatch = content.match(/ARRENDATARIO: __________\s*$/)
    if (sigMatch) {
      suggestions.push({
        id: uuid(),
        original_text: 'ARRENDATARIO: __________',
        suggested_text: `ARRENDATARIO: ${fields.tenantName}`,
        reason: locale === 'en'
          ? 'Fill in tenant signature as requested.'
          : 'Completar firma del arrendatario.',
        category: 'clarity',
        severity: 'medium'
      })
    }
  }

  // Amount
  if (fields.amount) {
    const amountMatch = content.match(/\$__________\s*(?:M\/CTE)?/)
    if (amountMatch) {
      suggestions.push({
        id: uuid(),
        original_text: amountMatch[0],
        suggested_text: `$${fields.amount} M/CTE`,
        reason: locale === 'en'
          ? 'Fill in rental amount as requested.'
          : 'Completar monto del canon según lo solicitado.',
        category: 'clarity',
        severity: 'high'
      })
    }
  }

  // Address
  if (fields.address) {
    const addressMatch = content.match(/inmueble ubicado en __________/)
    if (addressMatch) {
      suggestions.push({
        id: uuid(),
        original_text: addressMatch[0],
        suggested_text: `inmueble ubicado en ${fields.address}`,
        reason: locale === 'en'
          ? 'Fill in property address as requested.'
          : 'Completar dirección del inmueble según lo solicitado.',
        category: 'clarity',
        severity: 'high'
      })
    }
  }

  // Generic text replacements (e.g., "cambia nombre por Angélica")
  for (const r of replacements) {
    // Exact match
    if (content.indexOf(r.original) !== -1) {
      suggestions.push({
        id: uuid(),
        original_text: r.original,
        suggested_text: r.replacement,
        reason: locale === 'en'
          ? `Replace "${r.original}" with "${r.replacement}" as requested.`
          : `Reemplazar "${r.original}" por "${r.replacement}" según lo solicitado.`,
        category: 'terminology',
        severity: 'high'
      })
    } else {
      // Case-insensitive fallback
      const lowerContent = content.toLowerCase()
      const lowerOrig = r.original.toLowerCase()
      const idx = lowerContent.indexOf(lowerOrig)
      if (idx !== -1) {
        const actualText = content.substring(idx, idx + r.original.length)
        suggestions.push({
          id: uuid(),
          original_text: actualText,
          suggested_text: r.replacement,
          reason: locale === 'en'
            ? `Replace "${actualText}" with "${r.replacement}" as requested.`
            : `Reemplazar "${actualText}" por "${r.replacement}" según lo solicitado.`,
          category: 'terminology',
          severity: 'high'
        })
      }
    }
  }

  return suggestions
}

export async function mockGenerateSuggestions(request: GenerateSuggestionsRequest): Promise<GenerateSuggestionsResponse> {
  await delay(1200)
  // Normalize \x0B (Word soft returns) to spaces so original_text in suggestions
  // doesn't contain characters that Word's body.search() can't match
  const content = request.document_content.replace(/[\x0B\r\x0C]/g, ' ')
  const locale = request.locale

  const suggestions: GenerateSuggestionsResponse['suggestions'] = []

  // === Context-aware suggestions from conversation ===
  // If the user asked to update fields (date, names, etc.), generate suggestions for those
  if (request.conversation_id) {
    const conv = conversations.get(request.conversation_id)
    if (conv?.messages.length) {
      const contextual = generateContextualSuggestions(content, conv.messages, locale)
      suggestions.push(...contextual)
    }
  }

  // If context-aware suggestions were generated, return them directly
  // (don't dilute with generic style suggestions)
  if (suggestions.length > 0) {
    return { suggestion_set_id: uuid(), suggestions }
  }

  // === Pattern-based suggestions (for known document shapes) ===

  if (content.includes('...')) {
    suggestions.push({
      id: uuid(),
      original_text: '...',
      suggested_text: locale === 'en'
        ? '[Describe specific task here]'
        : '[Describir tarea específica aquí]',
      reason: locale === 'en'
        ? 'Placeholder items should be replaced with specific, actionable task descriptions.'
        : 'Los elementos de marcador de posición deben reemplazarse con descripciones de tareas específicas y accionables.',
      category: 'clarity',
      severity: 'high',
      position_hint: { paragraph_index: 3, context_before: '• ', context_after: '' }
    })
  }

  if (content.includes('To do')) {
    suggestions.push({
      id: uuid(),
      original_text: 'To do',
      suggested_text: locale === 'en' ? 'Pending Tasks' : 'Tareas Pendientes',
      reason: locale === 'en'
        ? 'Using a more formal heading improves document professionalism.'
        : 'Usar un encabezado más formal mejora el profesionalismo del documento.',
      category: 'style',
      severity: 'low',
      position_hint: { paragraph_index: 2 }
    })
  }

  if (content.includes('Completadas')) {
    suggestions.push({
      id: uuid(),
      original_text: 'Completadas',
      suggested_text: locale === 'en' ? 'Completed Tasks' : 'Tareas Completadas',
      reason: locale === 'en'
        ? 'Adding "Tasks" makes the heading more descriptive and consistent.'
        : 'Agregar "Tareas" hace el encabezado más descriptivo y consistente.',
      category: 'style',
      severity: 'low'
    })
  }

  if (content.includes('febrero 22') || content.includes('February 22')) {
    suggestions.push({
      id: uuid(),
      original_text: content.includes('febrero 22') ? 'febrero 22' : 'February 22',
      suggested_text: locale === 'en' ? 'February 22, 2026' : '22 de febrero de 2026',
      reason: locale === 'en'
        ? 'Include the full date with year for clarity and proper formatting.'
        : 'Incluir la fecha completa con año para mayor claridad y formato adecuado.',
      category: 'grammar',
      severity: 'medium'
    })
  }

  if (content.includes('Tip:') || content.includes('<Tip')) {
    suggestions.push({
      id: uuid(),
      original_text: locale === 'en'
        ? '<Tip: move each task to the Completed section once finished>'
        : '<Tip: mover cada tarea a la sección Completado una vez finalizada>',
      suggested_text: locale === 'en'
        ? '**Note:** Move each task to the "Completed" section once finished.'
        : '**Nota:** Mover cada tarea a la sección "Completadas" una vez finalizada.',
      reason: locale === 'en'
        ? 'Use bold "Note:" prefix instead of angle brackets for better readability.'
        : 'Usar prefijo "Nota:" en negrita en lugar de paréntesis angulares para mejor legibilidad.',
      category: 'structure',
      severity: 'medium'
    })
  }

  // === Generic suggestions — analyse any document content ===
  // Only run generic generators if we haven't found enough pattern-based ones
  if (suggestions.length < 3) {
    for (const gen of genericGenerators) {
      try {
        const s = gen(content, locale)
        if (s) suggestions.push(s)
      } catch { /* skip failing generator */ }
      if (suggestions.length >= 5) break
    }
  }

  return {
    suggestion_set_id: uuid(),
    suggestions
  }
}

export async function mockSubmitFeedback(): Promise<void> {
  await delay(300)
  debugLog.add('info', 'Mock: Feedback submitted successfully')
}

// Exports for testing
export const _testing = {
  isSummaryRequest,
  isCreationRequest,
  isQuestionAboutDocument,
  isUpdateRequest,
  isAddRequest,
  summarizeDocument,
  describeDocument,
  detectDocumentType,
  generateMessageAwareAnalysis,
  isGreeting,
  greetWithDocumentContext,
  respondToFollowUp,
  analyzeContent,
  conversations,
}
